import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import './css/browse_jobs.css';

const TYPES = ["Full-time", "Part-time", "Freelance", "Contract"];

export default function BrowseJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [dbSpecs, setDbSpecs] = useState([]);
  const [dbCities, setDbCities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [types, setTypes] = useState([]);
  const [spec, setSpec] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [sort, setSort] = useState("match");
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleType = (t) => setTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // FIX: Force immediate state reset AND fetch directly with baseline defaults
  const clearFilters = (e) => {
    if (e) e.preventDefault(); 
    
    // 1. Reset all visual states
    setQuery("");
    setCity("");
    setSpec("");
    setTypes([]);
    setSalaryMin("");
    setSalaryMax("");
    setSort("match");

    // 2. Fire an immediate clean fetch bypassing the debounce loop delay
    fetchJobsWithParams({
      query: "",
      city: "",
      spec: "",
      types: [],
      minSalary: "",
      maxSalary: "",
      sort: "match"
    });
  };

  // 1. Fetch Lookups from DB Dimensions on Mount
  useEffect(() => {
    const fetchDimensions = async () => {
      const token = localStorage.getItem("token");
      const headers = { "Authorization": `Bearer ${token}` };
      
      try {
        const [specsRes, citiesRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIMENSIONS}/specialisations`, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`, { headers }).catch(() => null)
        ]);

        if (specsRes && specsRes.ok) {
          const specsData = await specsRes.json();
          setDbSpecs(specsData);
        }
        
        if (citiesRes && citiesRes.ok) {
          const citiesData = await citiesRes.json();
          setDbCities(citiesData);
        } else {
          setDbCities(["Nairobi", "Mombasa", "Westlands", "Karen", "Kilimani"]);
        }
      } catch (err) {
        console.error("Error loading form filter dropdown dimensions:", err);
      }
    };
    fetchDimensions();
  }, []);

  // 2. Refactored Parameterized Query Fetch Pipeline
  const fetchJobsWithParams = async (overrideParams) => {
    setLoading(true);
    const token = localStorage.getItem("token");

    // Use passed parameters if clearing, otherwise fall back to active state variables
    const activeQuery = overrideParams ? overrideParams.query : query;
    const activeCity = overrideParams ? overrideParams.city : city;
    const activeSpec = overrideParams ? overrideParams.spec : spec;
    const activeTypes = overrideParams ? overrideParams.types : types;
    const activeMin = overrideParams ? overrideParams.minSalary : salaryMin;
    const activeMax = overrideParams ? overrideParams.maxSalary : salaryMax;
    const activeSort = overrideParams ? overrideParams.sort : sort;
    
    const params = new URLSearchParams({
      query: activeQuery,
      city: activeCity,
      spec: activeSpec,
      types: activeTypes.join(','),
      minSalary: activeMin,
      maxSalary: activeMax,
      sort: activeSort
    });

    try {
      const res = await fetch(`${API_ENDPOINTS.EMPBROWSEJOBS}?${params}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (err) {
      console.error("Failed to fetch jobs", err);
    } finally {
      setLoading(false);
    }
  };

  // Standard runtime fetch that uses state
  const fetchJobs = () => fetchJobsWithParams();

  // 3. Debounce Input Side-Effect Listener
  useEffect(() => {
    const timer = setTimeout(() => fetchJobs(), 300);
    return () => clearTimeout(timer);
  }, [query, city, types, spec, salaryMin, salaryMax, sort]);

  const toggleSave = async (id) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_ENDPOINTS.JOBS}/${id}/save`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (res.ok) {
      setJobs(prev => prev.map(j => j.id === id ? { ...j, isSaved: !j.isSaved } : j));
    }
  };

  const getJobIcon = (category) => {
    const icons = {
      "Hair Styling": "✂️",
      "Nail Technology": "💅",
      "Esthetics / Skincare": "🧖",
      "Makeup Artistry": "💄",
      "Barbering": "🪒",
      "Lash & Brow": "👁️",
      "Beauty Education": "🏫",
      "Salon Management": "💼",
    };
    return icons[category] || "✨";
  };

  return (
    <JSLayout title="Browse" titleEm="Jobs">
      {/* Search Input Bar Layer */}
      <div className="bj-search-bar">
        <div className="bj-search-wrap">
          <span className="bj-search-icon">🔍</span>
          <input 
            className="bj-search-input" 
            placeholder="Search job title, salon, or keywords…" 
            value={query} 
            onChange={e => setQuery(e.target.value)} 
          />
        </div>
        {/* <button className="bj-search-btn" onClick={fetchJobs}>Search</button> */}
      </div>

      <div className="bj-layout">
        
        {/* Left Side Filter Selection Panel */}
        <div className={`bj-filters${filterOpen ? " mobile-open" : ""}`}>
          <div className="bj-filter-close">
            <div className="bj-filter-title">Filter <em>jobs</em></div>
            <button className="bj-filter-close-btn" onClick={() => setFilterOpen(false)}>✕</button>
          </div>

          <div className="bj-filter-group">
            <span className="bj-filter-label">Location</span>
            <select className="bj-filter-select" value={city} onChange={e => setCity(e.target.value)}>
              <option value="">All locations</option>
              {dbCities.map(c => (
                <option key={c?.id || c} value={c?.name || c}>{c?.name || c}</option>
              ))}
            </select>
          </div>

          <div className="bj-filter-group">
            <span className="bj-filter-label">Specialisation</span>
            <select className="bj-filter-select" value={spec} onChange={e => setSpec(e.target.value)}>
              <option value="">All specialisations</option>
              {dbSpecs.map(s => (
                <option key={s?.id || s} value={s?.name || s}>{s?.name || s}</option>
              ))}
            </select>
          </div>

          <div className="bj-filter-group">
            <span className="bj-filter-label">Job Type</span>
            <div className="bj-check-group">
              {TYPES.map(t => (
                <label key={t} className="bj-check-item">
                  <input type="checkbox" checked={types.includes(t)} onChange={() => toggleType(t)} />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bj-filter-group">
            <span className="bj-filter-label">Salary Range (KSh/mo)</span>
            <div className="bj-salary-container-aligned">
              <input 
                className="bj-salary-input-aligned" 
                type="number" 
                placeholder="Min" 
                value={salaryMin} 
                onChange={e => setSalaryMin(e.target.value)} 
              />
              <span className="bj-salary-separator">至</span>
              <input 
                className="bj-salary-input-aligned" 
                type="number" 
                placeholder="Max" 
                value={salaryMax} 
                onChange={e => setSalaryMax(e.target.value)} 
              />
            </div>
          </div>

          {/* FIX: Explicit type configuration to prevent accidental form triggers */}
          <button type="button" className="bj-filter-clear" onClick={clearFilters}>Clear all filters</button>
        </div>

        {/* Right Side Core Results Area Grid */}
        <div style={{ flex: 1 }}>
          <div className="bj-results-hdr">
            <div className="bj-results-count">
              {loading ? "Updating list..." : <span><strong>{jobs.length}</strong> listings discovered</span>}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="bj-mobile-filter-trigger" onClick={() => setFilterOpen(true)}>⚙ Filters</button>
              <select className="bj-sort-select" value={sort} onChange={e => setSort(e.target.value)}>
                {/* <option value="match">Best match</option> */}
                <option value="recent">Most recent</option>
                <option value="salary">Highest salary</option>
              </select>
            </div>
          </div>

          {loading && jobs.length === 0 ? (
            <div className="bj-loading-shimmer-box">Loading matching listings...</div>
          ) : !loading && jobs.length === 0 ? (
            <div className="bj-empty">
              <div className="bj-empty-icon">🔍</div>
              <div className="bj-empty-title">No matching listings found</div>
              <div className="bj-empty-sub">Try expanding your filters or adjusting your keywords.</div>
            </div>
          ) : (
            <div className={`bj-job-list ${loading ? "bj-loading-fade" : ""}`}>
              {jobs.map(job => (
                <div key={job.id} className="bj-job-card" onClick={() => navigate(`/jobs/${job.id}`)}>
                  <div className="bj-job-logo">{getJobIcon(job.spec || job.specialisation)}</div>
                  <div className="bj-job-body">
                    <div className="bj-job-title">{job.title}</div>
                    <div className="bj-job-company">{job.company}</div>
                    <div className="bj-job-tags">
                      <span className="bl-tag bl-tag-loc">📍 {job.location}</span>
                      <span className="bl-tag bl-tag-type">{job.type}</span>
                      {job.match && <span className="bl-tag bl-tag-match">✨ {job.match}% match</span>}
                    </div>
                    <div className="bj-job-desc">{job.desc || job.description}</div>
                  </div>
                  <div className="bj-job-right">
                    <div className="bj-job-salary">{job.salary}</div>
                    <div className="bj-job-posted">{job.posted}</div>
                    <button 
                      className={`bj-save-btn ${job.isSaved ? "saved" : ""}`}
                      onClick={e => { e.stopPropagation(); toggleSave(job.id); }}
                    >
                      {job.isSaved ? "♥ Saved" : "♡ Save"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </JSLayout>
  );
}